import {AggregatedAiAttributeValue, AiAttributeValueEntry} from "../../domain/VulSourceItem";
import {Link} from "react-router-dom";
import {BsSearch} from "react-icons/bs";
import {FiExternalLink} from "react-icons/fi";
import {Tooltip} from "antd";
import {AiExtractorEntryList} from "../common/AiAttributeRating";
import {FormPersister} from "../../sal-ui/FormPersister";

interface Props {
    label: string;
    aiAttributeValue: AggregatedAiAttributeValue;
    aiAttributeTagClassName: string;
    aiAttributeTagInteractiveClassName: string;
}

function AiAttributeTextTag(props: Props) {
    const {label, aiAttributeValue, aiAttributeTagClassName, aiAttributeTagInteractiveClassName} = props;
    const entries = aiAttributeValue.values ?? [];

    if (aiAttributeValue.valueRenderType === "CVE" || aiAttributeValue.valueRenderType === "URL") {
        const values = dedupeStrings(entries.flatMap(e => getTextValues(e.valueTexts, e.valueText)));

        if (values.length === 0) return null;

        const tooltipContent = (
            <AiExtractorEntryList entries={entries} renderValue={getEntryDisplayText}/>
        );

        if (aiAttributeValue.valueRenderType === "CVE") {
            return <>
                {values.map((value, index) => {
                    const cveId = normalizeCveId(value);

                    return (
                        <Tooltip key={`${label}_${index}`} title={tooltipContent} overlayStyle={{maxWidth: "none"}}>
                            <span className={cveId ? `${aiAttributeTagClassName} ${aiAttributeTagInteractiveClassName}` : aiAttributeTagClassName}>
                                {value}
                                {cveId && <>
                                    <Link to={`/vul-source-items?${createCveFilterParam(cveId)}`} target={"_blank"} rel={"noopener noreferrer"}>
                                        <BsSearch className={"anticon"} title={"Search in items"}/>
                                    </Link>
                                    <a href={`https://nvd.nist.gov/vuln/detail/${cveId}`} target={"_blank"} rel={"noopener noreferrer"}>
                                        <FiExternalLink className={"anticon"} title={"Open NVD"}/>
                                    </a>
                                </>}
                            </span>
                        </Tooltip>
                    );
                })}
            </>;
        }

        return <>
            {values.map((value, index) => {
                const safeUrl = toSafeHttpUrl(value);

                return (
                    <Tooltip key={`${label}_${index}`} title={tooltipContent} overlayStyle={{maxWidth: "none"}}>
                        <span className={safeUrl ? `${aiAttributeTagClassName} ${aiAttributeTagInteractiveClassName}` : aiAttributeTagClassName}>
                            <b>{label}</b>: {safeUrl
                                ? <a href={safeUrl} target={"_blank"} rel={"noopener noreferrer"}>{value}</a>
                                : value}
                        </span>
                    </Tooltip>
                );
            })}
        </>;
    }

    // Plain text: collect all text values from all AI extractors
    const textValues = dedupeStrings(entries.map(getEntryDisplayText).filter(Boolean));

    if (textValues.length === 0) return null;

    const formatted = textValues.length === 1
        ? textValues[0]
        : textValues.join("·");

    const tooltipContent = (
        <AiExtractorEntryList entries={entries} renderValue={getEntryDisplayText}/>
    );

    return (
        <Tooltip title={tooltipContent} overlayStyle={{maxWidth: "none"}}>
            <span className={aiAttributeTagClassName}>
                <b>{label}</b>: {formatted}
            </span>
        </Tooltip>
    );
}

function getEntryDisplayText(e: AiAttributeValueEntry): string {
    return e.valueText ?? (Array.isArray(e.valueTexts) ? e.valueTexts.join(", ") : "");
}

function getTextValues(valueTexts: string[] | undefined, valueText: string | undefined): string[] {
    if (Array.isArray(valueTexts) && valueTexts.length > 0) {
        return valueTexts;
    }

    if (valueText) {
        return [valueText];
    }

    return [];
}

function dedupeStrings(values: string[]): string[] {
    return [...new Set(values)];
}

function normalizeCveId(value: string): string | undefined {
    const normalized = value.trim().toUpperCase();

    return /^CVE-\d{4}-\d{4,}$/.test(normalized) ? normalized : undefined;
}

function toSafeHttpUrl(value: string): string | undefined {
    const trimmed = value.trim();

    if (!trimmed) {
        return undefined;
    }

    try {
        const url = new URL(trimmed);

        return url.protocol === "http:" || url.protocol === "https:"
            ? url.toString()
            : undefined;
    } catch {
        return undefined;
    }
}

function createCveFilterParam(cveId: string): string {
    const filter = {
        typeHints: {},
        values: {cveId}
    };

    const searchParams = new URLSearchParams();

    searchParams.set("filter", FormPersister.encodeUrlState(filter));
    searchParams.set("$readOnly", "");

    return searchParams.toString();
}

export default AiAttributeTextTag;
